/**
 * MultiSelectInput Component
 *
 * A comprehensive multi-select input component that displays selected values as capsules
 * and opens a full-screen dialog for option selection. Follows BiteWise design patterns
 * and accessibility guidelines.
 *
 * @module components/ui/MultiSelectInput
 */

import React, { forwardRef, useState, useCallback, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { useTheme } from '../../hooks/useTheme';
import { Button } from './Button';

/**
 * Option interface for MultiSelectInput
 */
export interface MultiSelectOption {
  /** Unique identifier for the option */
  value: string;
  /** Display label for the option */
  label: string;
  /** Whether the option is disabled */
  disabled?: boolean;
}

/**
 * MultiSelectInput component props interface
 */
export interface MultiSelectInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'value' | 'onChange'> {
  /** Input variant for different visual styles */
  variant?: 'default' | 'filled' | 'outlined';
  /** Input size */
  size?: 'sm' | 'md' | 'lg';
  /** Validation state */
  state?: 'default' | 'error' | 'warning' | 'success';
  /** Error message to display */
  error?: string;
  /** Warning message to display */
  warning?: string;
  /** Success message to display */
  success?: string;
  /** Helper text to display below input */
  helperText?: string;
  /** Label for the input */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Loading state */
  loading?: boolean;
  /** Full width */
  fullWidth?: boolean;
  /** Custom container class */
  containerClassName?: string;
  /** Custom label class */
  labelClassName?: string;
  /** Custom message class */
  messageClassName?: string;
  /** Suppress internal message display (when wrapped in FormField) */
  suppressMessage?: boolean;
  /** Array of available options */
  options: MultiSelectOption[];
  /** Array of selected values */
  value: string[];
  /** Callback when selection changes */
  onChange: (selectedValues: string[]) => void;
  /** Placeholder text when no items are selected */
  placeholder?: string;
  /** Maximum number of capsules to show before showing count */
  maxVisibleCapsules?: number;
  /** Dialog title */
  dialogTitle?: string;
  /** Search placeholder in dialog */
  searchPlaceholder?: string;
  /** Enable search functionality in dialog */
  searchable?: boolean;
}

/**
 * Capsule component for displaying selected values
 */
interface CapsuleProps {
  label: string;
  onRemove: () => void;
  isDark: boolean;
  size: 'sm' | 'md' | 'lg';
}

const Capsule: React.FC<CapsuleProps> = ({ label, onRemove, isDark, size }) => {
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium transition-colors',
        sizeClasses[size],
        isDark
          ? 'bg-primary-900/50 text-primary-200 border border-primary-700'
          : 'bg-primary-100 text-primary-800 border border-primary-200'
      )}
    >
      <span className="truncate max-w-[120px]">{label}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={cn(
          'ml-1.5 inline-flex items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-primary-500',
          size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : 'h-5 w-5',
          isDark
            ? 'text-primary-300 hover:bg-primary-800 hover:text-primary-100'
            : 'text-primary-600 hover:bg-primary-200 hover:text-primary-800'
        )}
        aria-label={`Remove ${label}`}
      >
        <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </span>
  );
};

/**
 * Full-screen dialog component for option selection
 */
interface SelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  options: MultiSelectOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  title: string;
  searchPlaceholder: string;
  searchable: boolean;
  isDark: boolean;
}

const SelectionDialog: React.FC<SelectionDialogProps> = ({
  isOpen,
  onClose,
  options,
  selectedValues,
  onSelectionChange,
  title,
  searchPlaceholder,
  searchable,
  isDark
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelectedValues, setLocalSelectedValues] = useState<string[]>(selectedValues);

  // Update local state when props change
  useEffect(() => {
    setLocalSelectedValues(selectedValues);
  }, [selectedValues]);

  // Filter options based on search term
  const filteredOptions = searchable
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const handleToggleOption = (value: string) => {
    setLocalSelectedValues(prev => {
      if (prev.includes(value)) {
        // Remove the value
        return prev.filter(v => v !== value);
      } else {
        // Add the value and maintain order based on options array
        const newSelection = [...prev, value];
        // Sort based on the order in the options array
        return options
          .filter(option => newSelection.includes(option.value))
          .map(option => option.value);
      }
    });
  };

  const handleConfirm = () => {
    onSelectionChange(localSelectedValues);
    onClose();
  };

  const handleCancel = () => {
    setLocalSelectedValues(selectedValues);
    onClose();
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, selectedValues, handleCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
    >
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 transition-opacity',
          isDark ? 'bg-black/80' : 'bg-black/50'
        )}
        onClick={handleCancel}
      />

      {/* Dialog Content */}
      <div
        className={cn(
          'relative w-full max-w-md max-h-[80vh] flex flex-col transition-all rounded-xl shadow-2xl',
          isDark ? 'bg-secondary-900' : 'bg-white'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-center justify-between p-4 border-b',
            isDark ? 'border-secondary-700' : 'border-secondary-200'
          )}
        >
          <h2
            id="dialog-title"
            className={cn(
              'text-lg font-semibold',
              isDark ? 'text-secondary-100' : 'text-secondary-900'
            )}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={handleCancel}
            className={cn(
              'p-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500',
              isDark
                ? 'text-secondary-400 hover:bg-secondary-800 hover:text-secondary-200'
                : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-800'
            )}
            aria-label="Close dialog"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        {searchable && (
          <div className="p-4">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                'w-full px-4 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500',
              isDark
                  ? 'bg-secondary-800 border-secondary-600 text-secondary-100 placeholder:text-secondary-400'
                  : 'bg-white border-secondary-300 text-secondary-900 placeholder:text-secondary-200'
              )}
            />
          </div>
        )}

        {/* Options List */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {filteredOptions.map((option) => (
              <label
                key={option.value}
                className={cn(
                  'flex items-center p-3 rounded-lg cursor-pointer transition-colors',
                  option.disabled && 'opacity-50 cursor-not-allowed',
                  !option.disabled && (
                    isDark
                      ? 'hover:bg-secondary-800'
                      : 'hover:bg-secondary-50'
                  )
                )}
              >
                <input
                  type="checkbox"
                  checked={localSelectedValues.includes(option.value)}
                  onChange={() => !option.disabled && handleToggleOption(option.value)}
                  disabled={option.disabled}
                  className={cn(
                    'w-5 h-5 rounded border-2 transition-colors focus:ring-2 focus:ring-primary-500',
                    isDark
                      ? 'border-secondary-600 bg-secondary-800 text-primary-500'
                      : 'border-secondary-300 bg-white text-primary-600'
                  )}
                />
                <span
                  className={cn(
                    'ml-3 text-base',
                    isDark ? 'text-secondary-100' : 'text-secondary-900'
                  )}
                >
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          className={cn(
            'flex items-center justify-between p-4 border-t',
            isDark ? 'border-secondary-700' : 'border-secondary-200'
          )}
        >
          <span
            className={cn(
              'text-sm',
              isDark ? 'text-secondary-400' : 'text-secondary-600'
            )}
          >
            {localSelectedValues.length} selected
          </span>
          <div className="flex space-x-3">
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              Confirm Selection
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Accessible MultiSelectInput component with full-screen dialog selection
 *
 * @example
 * ```tsx
 * <MultiSelectInput
 *   label="Select Categories"
 *   options={[
 *     { value: 'tech', label: 'Technology' },
 *     { value: 'health', label: 'Health' },
 *     { value: 'finance', label: 'Finance' }
 *   ]}
 *   value={selectedCategories}
 *   onChange={setSelectedCategories}
 *   placeholder="Choose categories..."
 *   searchable
 * />
 * ```
 */
export const MultiSelectInput = forwardRef<HTMLInputElement, MultiSelectInputProps>(
  ({
    className,
    containerClassName,
    labelClassName,
    messageClassName,
    variant = 'default',
    size = 'md',
    state = 'default',
    error,
    warning,
    success,
    helperText,
    label,
    required = false,
    leftIcon,
    fullWidth = false,
    suppressMessage = false,
    options,
    value,
    onChange,
    placeholder = 'Select options...',
    maxVisibleCapsules = 3,
    dialogTitle = 'Select Options',
    searchPlaceholder = 'Search options...',
    searchable = true,
    id,
    'aria-describedby': ariaDescribedBy,
    ...props
  }, ref) => {
    const { isDark } = useTheme();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Generate unique IDs for accessibility
    const inputId = id || `multi-select-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${inputId}-error`;
    const warningId = `${inputId}-warning`;
    const successId = `${inputId}-success`;
    const helperId = `${inputId}-helper`;

    // Determine which message to show (priority: error > warning > success > helper)
    const message = error || warning || success || helperText;
    const messageId = error ? errorId : warning ? warningId : success ? successId : helperId;

    // Build aria-describedby
    const describedBy = [
      ariaDescribedBy,
      message ? messageId : undefined
    ].filter(Boolean).join(' ') || undefined;

    // Get selected options for display
    const selectedOptions = options.filter(option => value.includes(option.value));

    // Handle capsule removal
    const handleRemoveCapsule = useCallback((valueToRemove: string) => {
      onChange(value.filter(v => v !== valueToRemove));
    }, [value, onChange]);

    // Handle dialog selection change
    const handleSelectionChange = useCallback((newValues: string[]) => {
      onChange(newValues);
    }, [onChange]);

    // Base input classes
    const baseInputClasses = 'w-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 cursor-pointer';

    // Variant classes
    const variantClasses = {
      default: cn(
        'border border-gray-500',
        isDark ? 'bg-secondary-800' : 'bg-white'
      ),
      filled: cn(
        'border-0',
        isDark ? 'bg-secondary-700' : 'bg-secondary-100'
      ),
      outlined: cn(
        'border-2 border-gray-500 bg-transparent'
      )
    };

    // Size classes
    const sizeClasses = {
      sm: 'text-sm px-3 py-1.5 min-h-[32px] rounded-md',
      md: 'text-base px-4 py-2 min-h-[40px] rounded-lg',
      lg: 'text-lg px-5 py-3 min-h-[48px] rounded-lg'
    };

    // State classes
    const stateClasses = {
      default: cn(
        'focus:ring-primary-500/50',
        isDark ? 'focus:border-primary-400' : 'focus:border-primary-500'
      ),
      error: cn(
        'focus:ring-red-500/50',
        isDark ? 'border-red-400 focus:border-red-400' : 'border-red-500 focus:border-red-500'
      ),
      warning: cn(
        'focus:ring-yellow-500/50',
        isDark ? 'border-yellow-400 focus:border-yellow-400' : 'border-yellow-500 focus:border-yellow-500'
      ),
      success: cn(
        'focus:ring-green-500/50',
        isDark ? 'border-green-400 focus:border-green-400' : 'border-green-500 focus:border-green-500'
      )
    };

    // Text color classes
    const textClasses = cn(
      isDark ? 'text-secondary-100' : 'text-secondary-900'
    );

    // Icon classes
    const iconClasses = cn(
      'flex-shrink-0',
      isDark ? 'text-secondary-400' : 'text-secondary-500'
    );

    // Render capsules with overflow handling
    const renderCapsules = () => {
      if (selectedOptions.length === 0) {
        return (
          <span className={cn(
            'text-base',
            isDark ? 'text-secondary-400' : 'text-secondary-200'
          )}>
            {placeholder}
          </span>
        );
      }

      const visibleCapsules = selectedOptions.slice(0, maxVisibleCapsules);
      const remainingCount = selectedOptions.length - maxVisibleCapsules;

      return (
        <div className="flex flex-wrap gap-1.5">
          {visibleCapsules.map((option) => (
            <Capsule
              key={option.value}
              label={option.label}
              onRemove={() => handleRemoveCapsule(option.value)}
              isDark={isDark}
              size={size}
            />
          ))}
          {remainingCount > 0 && (
            <span
              className={cn(
                'inline-flex items-center rounded-full font-medium',
                size === 'sm' ? 'text-xs px-2 py-0.5' : size === 'md' ? 'text-sm px-2.5 py-1' : 'text-base px-3 py-1.5',
                isDark
                  ? 'bg-secondary-700 text-secondary-300 border border-secondary-600'
                  : 'bg-secondary-100 text-secondary-700 border border-secondary-200'
              )}
            >
              +{remainingCount} more
            </span>
          )}
        </div>
      );
    };

    return (
      <>
        <div className={cn('space-y-1', fullWidth && 'w-full', containerClassName)}>
          {/* Label */}
          {label && (
            <label
              htmlFor={inputId}
              className={cn(
                'block text-sm font-medium',
                isDark ? 'text-secondary-300' : 'text-secondary-700',
                required && 'after:content-["*"] after:ml-0.5 after:text-red-500',
                labelClassName
              )}
            >
              {label}
            </label>
          )}

          {/* Input Container */}
          <div className="relative">
            {/* Left Icon */}
            {leftIcon && (
              <div className={cn(
                'absolute left-3 top-1/2 transform -translate-y-1/2 z-10',
                iconClasses
              )}>
                {leftIcon}
              </div>
            )}

            {/* Input Field */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsDialogOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsDialogOpen(true);
                }
              }}
              className={cn(
                baseInputClasses,
                variantClasses[variant],
                sizeClasses[size],
                stateClasses[state],
                textClasses,
                leftIcon && 'pl-10',
                'pr-10', // Always reserve space for dropdown icon
                className
              )}
              aria-describedby={describedBy}
              aria-invalid={state === 'error' ? 'true' : 'false'}
              aria-required={required}
              aria-expanded={isDialogOpen}
              aria-haspopup="dialog"
              aria-label={label || 'Multi-select input'}
            >
              {renderCapsules()}
            </div>

            {/* Dropdown Icon */}
            <div className={cn(
              'absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none',
              iconClasses
            )}>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>

            {/* Hidden input for form compatibility */}
            <input
              ref={ref}
              type="hidden"
              id={inputId}
              value={value.join(',')}
              {...props}
            />
          </div>

          {/* Message - only show if not suppressed (e.g., when wrapped in FormField) */}
          {message && !suppressMessage && (
            <p
              id={messageId}
              className={cn(
                'text-sm',
                error && (isDark ? 'text-red-400' : 'text-red-600'),
                warning && (isDark ? 'text-yellow-400' : 'text-yellow-600'),
                success && (isDark ? 'text-green-400' : 'text-green-600'),
                !error && !warning && !success && (isDark ? 'text-secondary-400' : 'text-secondary-600'),
                messageClassName
              )}
              role={error ? 'alert' : undefined}
              aria-live={error ? 'polite' : undefined}
            >
              {message}
            </p>
          )}
        </div>

        {/* Selection Dialog */}
        <SelectionDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          options={options}
          selectedValues={value}
          onSelectionChange={handleSelectionChange}
          title={dialogTitle}
          searchPlaceholder={searchPlaceholder}
          searchable={searchable}
          isDark={isDark}
        />
      </>
    );
  }
);

MultiSelectInput.displayName = 'MultiSelectInput';

export default MultiSelectInput;